/**
 * A10: schema changes this build expects but the database hasn't applied. Two conditions, deliberately different:
 * storage missing entirely is a fresh install about to be set up (dismissible); some changes missing is a
 * half-upgraded product that will fail in odd places (not dismissible). Either way the fix is one command, offered
 * with copy-to-clipboard and a manual-selection fallback. Admins only.
 */
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { ReportControl } from '@/components/ui/ReportControl';

const COMMAND = 'npm run db:migrate';
// a preview build never changes the database: its waiting changes are applied when it is promoted (run/DEMO-ENV.md)
const PREVIEW = __PREVIEW__;

export function SchemaBanner() {
  const [dismissed, setDismissed] = useState(() => { try { return sessionStorage.getItem('rmp2.schema-banner') === 'dismissed'; } catch { return false; } });
  const [copyFailed, setCopyFailed] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const q = useQuery({
    queryKey: ['schema-status'],
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('schema_status');
      if (error) return { missing: error.code === 'PGRST202' || /Could not find the function/i.test(error.message), pending: [] as string[] };
      const applied = new Set(((data as { applied?: string[] })?.applied ?? []));
      return { missing: false, pending: __MIGRATIONS__.filter((m) => !applied.has(m)) };
    },
  });
  if (!q.data || (!q.data.missing && !q.data.pending.length)) return null;
  if (q.data.missing && dismissed) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(COMMAND); setCopyFailed(false); } catch { setCopyFailed(true); input.current?.select(); }
  };
  return (
    <div role="alert" className="z-banner border-b border-st-warning/40 bg-surface-2 px-4 py-2 text-sm text-ink" data-testid="schema-banner" data-kind={q.data.missing ? 'missing' : 'pending'}>
      <div className="relative mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1">
        <strong className="font-semibold">{q.data.missing ? 'This database hasn’t been set up yet.' : `${q.data.pending.length} database change${q.data.pending.length === 1 ? ' is' : 's are'} waiting to be applied — some features will fail until then.`}</strong>
        {PREVIEW ? (
          <span className="text-ink-2" data-testid="schema-banner-preview">They are applied when this preview goes live.</span>
        ) : (
          <>
            <span className="text-ink-2">Run</span>
            <input ref={input} readOnly value={COMMAND} aria-label="Command to run" onFocus={(e) => e.currentTarget.select()} className="h-7 w-44 rounded-control bg-surface px-2 font-mono text-[13px] ring-1 ring-line" />
            <button className="underline underline-offset-4" onClick={() => void copy()}>Copy</button>
            {copyFailed && <span className="text-[12px] text-ink-2">Copy didn’t work here — the command is selected; press Ctrl+C.</span>}
          </>
        )}
        {!q.data.missing && <span className="text-[12px] text-muted" title={q.data.pending.join('\n')}>({q.data.pending.slice(0, 2).join(', ')}{q.data.pending.length > 2 ? ', …' : ''})</span>}
        {/* one click tells the Room Map Pro team; the names of the waiting changes travel as the detail */}
        <ReportControl surface="state" className="static" failure={{ reason: 'load', message: q.data.missing ? 'This database hasn’t been set up yet.' : 'Database changes are waiting to be applied.', details: q.data.pending.join(', ') || undefined, apiPath: '/rest/v1/rpc/schema_status' }} />
        {q.data.missing && <button className="ml-auto text-[13px] text-ink-2 underline underline-offset-4" onClick={() => { try { sessionStorage.setItem('rmp2.schema-banner', 'dismissed'); } catch { /* not kept */ } setDismissed(true); }}>Dismiss</button>}
      </div>
    </div>
  );
}
